import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SignaturePad from './SignaturePad';
import signatureService from '../services/signature';

const ReceiptSigner = ({ receipt, onSigningComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savedSignature, setSavedSignature] = useState(null);
  const [landlordSignature, setLandlordSignature] = useState(null);
  const [tenantSignature, setTenantSignature] = useState(null);
  const [includeTenantSignature, setIncludeTenantSignature] = useState(false);

  // Fetch user's saved signature
  useEffect(() => {
    const fetchSignature = async () => {
      try {
        setLoading(true);
        const response = await signatureService.getUserSignature();
        if (response.exists) {
          setSavedSignature(response.signature.imageData);
          setLandlordSignature(response.signature.imageData);
        }
      } catch (error) {
        console.error('Failed to fetch signature:', error);
        toast.error('Failed to load your saved signature');
      } finally {
        setLoading(false);
      }
    };

    fetchSignature();
  }, []);

  const handleSignReceipt = async () => {
    if (!landlordSignature) {
      toast.error('Landlord signature is required');
      return;
    }

    try {
      setLoading(true);
      const signatures = {
        landlordSignature,
        tenantSignature: includeTenantSignature ? tenantSignature : '',
      };

      await signatureService.signReceipt(receipt.id, signatures);
      toast.success('Receipt signed successfully');

      if (onSigningComplete) {
        onSigningComplete();
      }
    } catch (error) {
      console.error('Failed to sign receipt:', error);
      // The signature service already extracts error.response.data, so we access error.error directly
      const errorMessage = error?.error || error?.message || 'Failed to sign receipt';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUseSavedSignature = () => {
    if (savedSignature) {
      setLandlordSignature(savedSignature);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="font-medium text-gray-900 mb-4">Sign Receipt #{receipt.receiptNo}</h3>

        {loading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Landlord Signature */}
            <div>
              {savedSignature && (
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-600">
                    You have a saved signature you can use.
                  </p>
                  <button
                    type="button"
                    onClick={handleUseSavedSignature}
                    className="px-3 py-1 text-sm bg-primary-100 text-primary-700 hover:bg-primary-200 rounded"
                  >
                    Use saved signature
                  </button>
                </div>
              )}

              <SignaturePad
                initialSignature={landlordSignature}
                onSave={setLandlordSignature}
                onClear={() => setLandlordSignature(null)}
                label="Landlord Signature"
                height={150}
              />
            </div>

            {/* Option to include tenant signature */}
            <div className="border-t pt-4">
              <div className="flex items-center mb-4">
                <input
                  id="includeTenantSignature"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={includeTenantSignature}
                  onChange={(e) => setIncludeTenantSignature(e.target.checked)}
                />
                <label htmlFor="includeTenantSignature" className="ml-2 block text-sm text-gray-900">
                  Include tenant signature (if tenant has signed a physical copy)
                </label>
              </div>

              {includeTenantSignature && (
                <SignaturePad
                  initialSignature={tenantSignature}
                  onSave={setTenantSignature}
                  onClear={() => setTenantSignature(null)}
                  label="Tenant Signature"
                  height={150}
                />
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate('/receipts')}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSignReceipt}
                className="btn-primary"
                disabled={loading || !landlordSignature}
              >
                {loading ? 'Signing...' : 'Sign Receipt'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">About Signing Receipts</h3>
        <ul className="list-disc text-sm text-blue-700 ml-5 space-y-1">
          <li>Your electronic signature will be permanently added to the receipt.</li>
          <li>The receipt cannot be modified after signing.</li>
          <li>You can download the signed receipt for your records.</li>
          <li>You can add the tenant's signature if they've signed a physical copy.</li>
        </ul>
      </div>
    </div>
  );
};

export default ReceiptSigner;